import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ColorVariant,
  CornerRadius,
  HoverEffectVariant,
  parseSpacing,
  RawSpacingInput,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos } from '@/core/models/app-components/types'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

function getInitialActiveTabId(componentId: string, defaultTabId: string) {
  const activeTabIds = JSON.parse(sessionStorage.getItem('activeTabIds') || '{}')
  return (activeTabIds[componentId] as string) ?? defaultTabId
}

interface CompTabsWrapperCoreProps {
  core: CoreComponentInfos
  children: (props: { id: string }) => React.ReactNode
}

/**
 * CompTabsWrapperCore
 */
export const CompTabsWrapperCore = React.memo(
  ({
    core: { component, settings, currentParentBg, element, allSettings = {} },
    children,
  }: CompTabsWrapperCoreProps) => {
    //
    const tabIds = useMemo(() => {
      return element?.children?.map((tab) => tab.id) || []
    }, [element])

    const componentId = element?.id || ''

    const [activeTabId, setActiveTabId] = useState(getInitialActiveTabId(componentId, tabIds[0]))

    const handleChangeTab = useCallback(
      (value: string) => {
        setActiveTabId(value)
        const activeTabIds = JSON.parse(sessionStorage.getItem('activeTabIds') || '{}')
        sessionStorage.setItem('activeTabIds', JSON.stringify({ ...activeTabIds, [componentId]: value }))
      },
      [componentId]
    )

    useEffect(() => {
      if (tabIds.length > 0) {
        if (!activeTabId || !tabIds.find((tabId) => tabId === activeTabId)) {
          handleChangeTab(tabIds[0])
        }
      }
    }, [activeTabId, handleChangeTab, tabIds])

    // ========================= data =========================
    const showIcon = settings[ComponentSettingKey.TabsDataShowIcon] ?? true
    const showLabel = settings[ComponentSettingKey.TabsDataShowLabel] ?? true

    // ========================= design =========================
    // base
    const width = settings[ComponentSettingKey.TabsDesignWidth][0] || '100%'
    const alignment = settings[ComponentSettingKey.TabsDesignAlign] || 'parent'
    const margin = settings[ComponentSettingKey.TabsDesignMargin] as RawSpacingInput

    const hoverEffect = settings[ComponentSettingKey.TabsDesignHoverEffect] as HoverEffectVariant

    // tabs
    const bgColor = getSafely(settings, [ComponentSettingKey.TabsDesignBgColor], 'primary-80') as ColorVariant
    const variant = settings[ComponentSettingKey.TabsDesignVariant] || 'default'
    const orientation = settings[ComponentSettingKey.TabsDesignOrientation] || 'horizontal'
    const contentAlignment = settings[ComponentSettingKey.TabsDesignContentAlign] || 'center'
    const cornerRadius = settings[ComponentSettingKey.TabsDesignShape] || ('medium' as CornerRadius)
    const tabsPaddings = parseSpacing(settings[ComponentSettingKey.TabsDesignPadding])
    const contentPaddings = parseSpacing(settings[ComponentSettingKey.TabsDesignContentPadding])
    const tabItemPaddings = parseSpacing(settings[ComponentSettingKey.TabsDesignItemPadding])
    const contentShadow = settings[ComponentSettingKey.TabsDesignContentShadow] || 'sm'

    return (
      <Tabs
        // base
        id={component._id}
        width={width}
        alignment={alignment}
        margin={margin}
        backgroundColor={currentParentBg}
        baseClassName='max-w-full overflow-hidden'
        // tabs
        value={activeTabId}
        onValueChange={handleChangeTab}
        orientation={orientation}
        hoverEffect={hoverEffect}
        hoverIntensity='md'
      >
        <TabsList
          variant={variant}
          tabsPaddings={tabsPaddings}
          backgroundColor={bgColor}
          pageBackgroundColor={currentParentBg}
        >
          {tabIds.map((id) => {
            const tab = allSettings[id]
            if (!tab) return null
            return (
              <TabsTrigger
                key={id}
                value={id}
                variant={variant}
                tabItemPaddings={tabItemPaddings}
                backgroundColor={bgColor}
                pageBackgroundColor={currentParentBg}
                icon={showIcon ? tab.icon : undefined}
                label={showLabel ? tab.title : undefined}
                hoverEffect={hoverEffect}
                hoverIntensity='md'
              />
            )
          })}
        </TabsList>

        {activeTabId && (
          <TabsContent
            key={activeTabId}
            value={activeTabId}
            elevation={contentShadow}
            contentAlignment={contentAlignment}
            contentPaddings={contentPaddings}
            cornerRadius={cornerRadius}
            backgroundColor={bgColor}
          >
            {children({ id: activeTabId })}
          </TabsContent>
        )}
      </Tabs>
    )
  }
)
CompTabsWrapperCore.displayName = 'CompTabsWrapperCore'
